/**
 * GraphPanel
 *
 * Reusable, configurable graph panel for the Nexus dashboard and contacts
 * pages (FEATURE-01/02/03). Renders a metric with an optional split
 * overlay as a line/bar/area/pie/funnel chart.
 *
 * Line chart uses AreaChart with a near-transparent fill so it is
 * always visible even when values are near-zero — matching the approach
 * used by nexus-chart-area-interactive on the same page.
 *
 * Dependencies:
 * - Recharts via components/ui/chart primitives
 * - lib/nexus/graph-metrics (metric catalog + config types)
 */

'use client'

import { useId, useMemo } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Funnel,
  FunnelChart,
  Legend,
  Pie,
  PieChart,
  XAxis,
  YAxis
} from 'recharts'

import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  getNexusMetric,
  resolveChartType,
  type NexusChartType,
  type NexusDateRangePreset,
  type NexusGraphPanelConfig
} from '@/lib/nexus/graph-metrics'

export type GraphPanelDatum = {
  label: string
  value: number
  split?: number
}

export interface GraphPanelProps {
  config: NexusGraphPanelConfig
  data: GraphPanelDatum[]
  loading?: boolean
  className?: string
  onConfigChange?: (config: NexusGraphPanelConfig) => void
  onRemove?: (id: string) => void
}

const DATE_RANGE_OPTIONS: { value: NexusDateRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom' }
]

// Literal hsl() so ChartContainer emits --color-value/--color-split that
// resolve in both light + dark. var(--chart-N) was undefined in this app.
// Same palette as nexus-chart-area-interactive so the page is one system.
const SERIES_VALUE_COLOR = 'hsl(142, 71%, 45%)' // emerald-500
const SERIES_SPLIT_COLOR = 'hsl(217, 91%, 60%)' // blue-500

const CATEGORY_COLORS = [
  'hsl(142, 71%, 45%)',
  'hsl(217, 91%, 60%)',
  'hsl(258, 90%, 66%)',
  'hsl(199, 89%, 48%)',
  'hsl(38,  92%, 50%)'
]

// --------------

export function GraphPanel({
  config,
  data,
  loading = false,
  className,
  onConfigChange,
  onRemove
}: GraphPanelProps) {
  const reactId = useId()
  const metric = getNexusMetric(config.metric)
  const splitMetric = config.split ? getNexusMetric(config.split) : undefined
  const chartType = resolveChartType(config.metric, config.chartType)

  const chartConfig = useMemo<ChartConfig>(
    () => ({
      value: { label: metric?.label ?? config.metric, color: SERIES_VALUE_COLOR },
      split: splitMetric
        ? { label: splitMetric.label, color: SERIES_SPLIT_COLOR }
        : { label: 'Split', color: SERIES_SPLIT_COLOR }
    }),
    [config.metric, metric?.label, splitMetric]
  )

  const updateDateRange = (next: NexusDateRangePreset) => {
    onConfigChange?.({ ...config, filters: { ...config.filters, dateRange: next } })
  }

  return (
    <section
      className={cn(
        'flex min-w-0 flex-col gap-4 rounded-2xl border-0 bg-muted/20 p-5 text-card-foreground shadow-none',
        className
      )}
      aria-label={config.title}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="truncate text-sm font-medium text-foreground">{config.title}</h3>
        <Select value={config.filters.dateRange} onValueChange={updateDateRange}>
          <SelectTrigger
            className="h-8 w-[132px] rounded-lg border-0 bg-background/60 text-xs shadow-none focus:ring-1 focus:ring-primary/30 focus:ring-offset-0"
            aria-label="Date range"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {DATE_RANGE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="rounded-lg">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      {loading ? (
        <div className="h-[240px] w-full animate-pulse rounded-xl bg-background/60" />
      ) : data.length === 0 ? (
        <div className="flex h-[240px] w-full items-center justify-center rounded-xl border border-dashed border-border/40 text-sm text-muted-foreground">
          No data for this range.
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="aspect-auto h-[240px] w-full">
          {renderChart(chartType, data, Boolean(config.split), reactId)}
        </ChartContainer>
      )}
    </section>
  )
}

// --------------

function renderChart(
  chartType: NexusChartType,
  data: GraphPanelDatum[],
  hasSplit: boolean,
  id: string
) {
  const vId = `gp-v-${id}`
  const sId = `gp-s-${id}`

  // ── Bar ───────────────────────────────────────────────────────────────
  if (chartType === 'bar') {
    return (
      <BarChart data={data}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.4} />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 11 }} />
        <YAxis tickLine={false} axisLine={false} width={32} tick={{ fontSize: 11 }} />
        <ChartTooltip content={<ChartTooltipContent />} cursor={false} />
        {hasSplit && (
          <Legend content={<ChartLegendContent />} verticalAlign="bottom" wrapperStyle={{ paddingTop: 8 }} />
        )}
        <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} />
        {hasSplit && <Bar dataKey="split" fill="var(--color-split)" radius={[4, 4, 0, 0]} />}
      </BarChart>
    )
  }

  // ── Pie ───────────────────────────────────────────────────────────────
  if (chartType === 'pie') {
    return (
      <PieChart>
        <ChartTooltip content={<ChartTooltipContent />} />
        <Pie data={data} dataKey="value" nameKey="label" innerRadius={48} paddingAngle={2}>
          {data.map((_, i) => (
            <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
          ))}
        </Pie>
      </PieChart>
    )
  }

  // ── Funnel ────────────────────────────────────────────────────────────
  if (chartType === 'funnel') {
    return (
      <FunnelChart>
        <ChartTooltip content={<ChartTooltipContent />} />
        <Funnel dataKey="value" data={data} isAnimationActive>
          {data.map((_, i) => (
            <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
          ))}
        </Funnel>
      </FunnelChart>
    )
  }

  // ── Line + Area ───────────────────────────────────────────────────────
  // Both use AreaChart. A plain LineChart hugs the x-axis for near-zero
  // data and looks empty. AreaChart with a gradient fill gives visual mass
  // at any data level — identical to how nexus-chart-area-interactive works.
  //
  // line  = thin stroke (2px) + near-transparent fill (15% top opacity)
  //         so the stroke is the focus, but it is never invisible
  // area  = same stroke + richer fill (70% top opacity)
  const isLine = chartType === 'line'
  const topOpacity = isLine ? 0.15 : 0.7

  return (
    <AreaChart data={data}>
      <defs>
        <linearGradient id={vId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor="var(--color-value)" stopOpacity={topOpacity} />
          <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0.02} />
        </linearGradient>
        {hasSplit && (
          <linearGradient id={sId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--color-split)" stopOpacity={topOpacity} />
            <stop offset="95%" stopColor="var(--color-split)" stopOpacity={0.02} />
          </linearGradient>
        )}
      </defs>
      <CartesianGrid vertical={false} strokeDasharray="3 3" opacity={0.4} />
      <XAxis
        dataKey="label"
        tickLine={false}
        axisLine={false}
        tickMargin={8}
        minTickGap={24}
        tick={{ fontSize: 11 }}
      />
      <YAxis tickLine={false} axisLine={false} width={32} tick={{ fontSize: 11 }} />
      <ChartTooltip content={<ChartTooltipContent />} cursor={false} />
      {hasSplit && (
        <Legend
          content={<ChartLegendContent />}
          verticalAlign="bottom"
          wrapperStyle={{ paddingTop: 8 }}
        />
      )}
      <Area
        dataKey="value"
        type="natural"
        stroke="var(--color-value)"
        strokeWidth={isLine ? 2 : 1.5}
        fill={`url(#${vId})`}
        dot={false}
      />
      {hasSplit && (
        <Area
          dataKey="split"
          type="natural"
          stroke="var(--color-split)"
          strokeWidth={isLine ? 2 : 1.5}
          fill={`url(#${sId})`}
          dot={false}
        />
      )}
    </AreaChart>
  )
}

export default GraphPanel
